import { Skeleton, Text } from '@mantine/core';
import { i18nKeys, useTranslation } from 'locales';
import { AnalyticsCard } from 'modules/Analytics/components/AnalyticsCard';
import { AgedBalanceBars } from 'modules/Analytics/components/charts';
import { OverdueTotal } from 'modules/Analytics/components/OverdueTotal';

import { useCompanyNameOf, useConsolidation } from './consolidation';

const NO_CREDITS = { '0': 0, '30': 0, '60': 0, '90': 0, notDue: 0 };

type GroupAgedBalanceProps = { selectedCompanies: ReadonlyArray<string> };

export const GroupAgedBalance = ({ selectedCompanies }: GroupAgedBalanceProps) => {
  const { t } = useTranslation();
  const companyNameOf = useCompanyNameOf();
  const { groupAnalytics, groupAnalyticsError, isGroupAnalyticsLoading } =
    useConsolidation(selectedCompanies);

  if (groupAnalyticsError)
    return (
      <AnalyticsCard title={t(i18nKeys.ANALYTICS.AGED_BALANCE.TITLE)}>
        <Text>{t(i18nKeys.GROUP.ANALYTICS_UNAVAILABLE)}</Text>
      </AnalyticsCard>
    );
  if (isGroupAnalyticsLoading)
    return (
      <AnalyticsCard title={t(i18nKeys.ANALYTICS.AGED_BALANCE.TITLE)}>
        <Skeleton h={240} mt="md" radius={8} aria-busy />
      </AnalyticsCard>
    );
  if (!groupAnalytics) return null;

  const { agedBalance, contributions, excludedCompanies } = groupAnalytics;

  if (!agedBalance)
    return (
      <AnalyticsCard title={t(i18nKeys.ANALYTICS.AGED_BALANCE.TITLE)}>
        <Text>{t(i18nKeys.GROUP.NO_STORED_AGED_BALANCE)}</Text>
      </AnalyticsCard>
    );

  return (
    <AnalyticsCard
      title={t(i18nKeys.ANALYTICS.AGED_BALANCE.TITLE)}
      subtitle={
        <Text size="sm" c="dimmed">
          {t(i18nKeys.GROUP.COUNTED_COMPANIES, { count: contributions.length })}
        </Text>
      }
      actions={
        <OverdueTotal
          amount={agedBalance['0'] + agedBalance['30'] + agedBalance['60'] + agedBalance['90']}
        />
      }
    >
      <AgedBalanceBars agedBalanceCompact={{ debits: agedBalance, credits: NO_CREDITS }} />
      {excludedCompanies.length > 0 && (
        <Text c="gray.6">
          {t(i18nKeys.GROUP.EXCLUDED_COMPANIES, {
            companies: excludedCompanies.map(companyNameOf).join(', '),
          })}
        </Text>
      )}
    </AnalyticsCard>
  );
};
